<template>
  <div class="rental-place-list">
    <div class="header">
      <h3>
        Support Expenses
        <HelpLink topic="rental-places" title="About support expenses" />
      </h3>
    </div>

    <form class="create-row" @submit.prevent="createRentalPlace">
      <input
        v-model="newRentalPlace.cost_number"
        type="text"
        placeholder="Cost number"
        aria-label="Cost number"
        class="create-input"
      />
      <LocationSelect
        v-model="newRentalPlace.location_id"
        :locations="locations"
        placeholder="Select location"
        aria-label="Location"
        class="create-select"
      />
      <button type="submit" class="add-button" :disabled="!canCreate || isCreating">
        <i class="bi bi-plus-lg"></i> Add
      </button>
    </form>

    <div class="search-filter-row">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search by cost number..."
        class="search-input"
      />
      <LocationSelect
        v-model="locationFilter"
        :locations="locations"
        placeholder="All locations"
        allowEmpty
        aria-label="Filter by location"
        class="filter-select"
      />
    </div>

    <table class="rental-place-table">
      <thead>
        <tr>
          <th @click="setSort('cost_number')">
            Cost number
            <i v-if="sortColumn === 'cost_number'" class="filter-arrow" :class="arrowClass"></i>
          </th>
          <th @click="setSort('location_name')">
            Location
            <i v-if="sortColumn === 'location_name'" class="filter-arrow" :class="arrowClass"></i>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="rentalPlace in paginatedRentalPlaces"
          :key="rentalPlace.id"
          class="rental-place-row"
          @click="openRentalPlace(rentalPlace.id)"
        >
          <td>{{ rentalPlace.cost_number }}</td>
          <td>{{ rentalPlace.location_name || '—' }}</td>
        </tr>
        <tr v-if="!isLoading && !filteredRentalPlaces.length">
          <td colspan="2" class="empty-row">No support expenses found.</td>
        </tr>
      </tbody>
    </table>

    <div v-if="totalPages > 1" class="pagination">
      <button
        v-for="page in totalPages"
        :key="page"
        :class="{ active: page === currentPage }"
        @click="currentPage = page"
      >
        {{ page }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { API_ENDPOINTS } from '@/services/config';
import HelpLink from '@/components/help/HelpLink.vue';
import LocationSelect from '@/components/LocationSelect.vue';

const PAGE_SIZE = 15;

export default {
  name: 'RentalPlaceList',
  components: { HelpLink, LocationSelect },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      rentalPlaces: [],
      locations: [],
      isLoading: false,
      isCreating: false,
      newRentalPlace: { cost_number: '', location_id: null },
      searchQuery: '',
      locationFilter: null,
      sortColumn: 'cost_number',
      sortDirection: 'asc',
      currentPage: 1,
    };
  },
  computed: {
    canCreate() {
      return Boolean(this.newRentalPlace.cost_number.trim() && this.newRentalPlace.location_id);
    },
    locationNames() {
      return Object.fromEntries(this.locations.map((location) => [location.id, location.name]));
    },
    filteredRentalPlaces() {
      const query = this.searchQuery.trim().toLowerCase();
      const direction = this.sortDirection === 'asc' ? 1 : -1;

      return this.rentalPlaces
        .map((rentalPlace) => ({
          ...rentalPlace,
          location_name: this.locationNames[rentalPlace.location_id] || '',
        }))
        .filter((rentalPlace) =>
          (!query || rentalPlace.cost_number.toLowerCase().includes(query))
          && (this.locationFilter === null || rentalPlace.location_id === this.locationFilter))
        .sort((a, b) => direction * String(a[this.sortColumn]).localeCompare(String(b[this.sortColumn]), undefined, { numeric: true }));
    },
    totalPages() {
      return Math.ceil(this.filteredRentalPlaces.length / PAGE_SIZE);
    },
    paginatedRentalPlaces() {
      const start = (this.currentPage - 1) * PAGE_SIZE;
      return this.filteredRentalPlaces.slice(start, start + PAGE_SIZE);
    },
    arrowClass() {
      return this.sortDirection === 'desc' ? 'bi bi-arrow-down' : 'bi bi-arrow-up';
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    locationFilter() {
      this.currentPage = 1;
    },
  },
  created() {
    this.fetchLocations();
    this.fetchRentalPlaces();
  },
  methods: {
    async fetchRentalPlaces() {
      this.isLoading = true;
      try {
        const response = await axios.get(API_ENDPOINTS.rentalPlaces);
        this.rentalPlaces = response.data;
      } catch (error) {
        this.toast.error('Failed to fetch support expenses');
        console.error('Failed to fetch rental places:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchLocations() {
      try {
        const response = await axios.get(API_ENDPOINTS.locations);
        this.locations = response.data;
      } catch (error) {
        this.toast.error('Failed to fetch locations');
        console.error('Failed to fetch locations:', error);
      }
    },
    async createRentalPlace() {
      if (!this.canCreate) return;
      this.isCreating = true;
      try {
        await axios.post(API_ENDPOINTS.rentalPlaces, {
          cost_number: this.newRentalPlace.cost_number.trim(),
          location_id: this.newRentalPlace.location_id,
        });
        this.toast.success('Support expense created');
        this.newRentalPlace = { cost_number: '', location_id: this.newRentalPlace.location_id };
        await this.fetchRentalPlaces();
      } catch (error) {
        this.toast.error(error?.response?.data?.message || 'Failed to create support expense');
        console.error('Failed to create rental place:', error);
      } finally {
        this.isCreating = false;
      }
    },
    setSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },
    openRentalPlace(id) {
      this.$router.push({ name: 'RentalPlaceDetailPage', params: { id } });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.rental-place-list {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    h3 {
      color: $primary-color;
    }
  }

  .create-row,
  .search-filter-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
  }

  .create-row {
    padding-bottom: 12px;
    border-bottom: 1px solid rgba($primary-color, 0.15);
  }

  .create-input,
  .search-input {
    flex: 1;
    margin: 0;
  }

  .create-select,
  .filter-select {
    flex: 0 0 220px;
    margin: 0;
  }

  .add-button {
    width: auto;
    margin: 0;
    padding: 8px 16px;
    background-color: $primary-color;
    color: #fff;
    border: none;
    border-radius: 5px;
    white-space: nowrap;
    cursor: pointer;

    &:hover:not(:disabled) {
      background-color: darken($primary-color, 10%);
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  .rental-place-table {
    width: 100%;

    th {
      background-color: $form-color;
      border: 1px solid $primary-color;
      cursor: pointer;
    }
  }

  .rental-place-row {
    background-color: $secondary-color;
    cursor: pointer;

    &:hover {
      background-color: $form-color;
    }
  }

  .empty-row {
    text-align: center;
    color: rgba($primary-color, 0.7);
    font-style: italic;
  }

  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 20px;

    button {
      margin: 0 5px;
      padding: 5px 10px;
      background-color: $primary-color;
      border: 1px solid $secondary-color;
      border-radius: 5px;
      cursor: pointer;

      &:hover,
      &.active {
        background-color: darken($primary-color, 10%);
      }
    }
  }
}
</style>
